import React, { useState } from 'react';
import { RegulatoryNewsExport, toDate, fetchRegulatoryNewsExportPdf } from '../../api/regulatoryNews';
import { downloadBlob } from './NewsletterModal';

interface Props {
  exports: RegulatoryNewsExport[];
  onClose: () => void;
}

const formatDateTime = (d: Date): string =>
  d.toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });

export const PreviousExportsModal: React.FC<Props> = ({ exports, onClose }) => {
  const [openingId, setOpeningId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleOpen = async (id: string) => {
    setOpeningId(id);
    setError(null);
    try {
      downloadBlob(await fetchRegulatoryNewsExportPdf(id));
    } catch {
      setError('Could not open this export. It may have been replaced by a newer one.');
    } finally {
      setOpeningId(null);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 520 }}>
        <div className="modal-header">
          <h3 style={{ margin: 0, fontSize: 16 }}>Previously generated</h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          <p style={{ fontSize: 13, color: 'var(--c-text-muted)', marginBottom: 12 }}>
            The last 4 newsletters and carousels are kept here. Older ones are removed automatically.
          </p>
          {exports.length === 0 ? (
            <p style={{ fontSize: 14, color: 'var(--c-text-muted)' }}>Nothing generated yet.</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {exports.map((e) => (
                <div
                  key={e.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    padding: '8px 10px',
                    border: '1px solid var(--c-border)',
                    borderRadius: 'var(--r)',
                    background: 'var(--c-surface)',
                  }}
                >
                  <span
                    style={{
                      fontSize: 11,
                      fontWeight: 600,
                      padding: '2px 8px',
                      borderRadius: 999,
                      whiteSpace: 'nowrap',
                      background: 'var(--c-primary-10)',
                      color: 'var(--c-primary)',
                    }}
                  >
                    {e.type === 'newsletter' ? 'Newsletter' : 'Carousel'}
                  </span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600 }}>{e.criteriaLabel}</div>
                    <div style={{ fontSize: 11, color: 'var(--c-text-muted)' }}>
                      {e.articleCount} article{e.articleCount === 1 ? '' : 's'} · {formatDateTime(toDate(e.createdAt))}
                    </div>
                  </div>
                  <button className="btn btn-sm btn-outline" onClick={() => handleOpen(e.id)} disabled={openingId !== null}>
                    {openingId === e.id ? 'Opening…' : 'Open'}
                  </button>
                </div>
              ))}
            </div>
          )}
          {error && <p style={{ color: 'var(--c-error, #b00020)', fontSize: 13, marginTop: 10 }}>{error}</p>}
        </div>
        <div className="modal-footer">
          <button className="btn btn-outline" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
};
